import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PageLayout } from '../../../components/layout/PageLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Separator } from '../../../components/ui/separator';
import { Alert, AlertDescription } from '../../../components/ui/alert';
import { Loader2, ArrowLeft, CreditCard, Users, Star, AlertCircle, CheckCircle } from 'lucide-react';
import { billingService } from '../../../services';
import { pricingTiers } from '../../../content/mvp';
import type { SubscribeToTierRequest, SubscriptionTier } from '@arrowise/shared';
import type { PageProps } from '../../../types/page';

const SubscriptionForm: React.FC<PageProps> = ({ nav = true, footer = false }) => {
    const { tier } = useParams<{ tier: SubscriptionTier }>();
    const navigate = useNavigate();

    const [plan, setPlan] = useState<any | null>(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success] = useState(false);

    // Form state for team subscriptions
    const [teamData, setTeamData] = useState({
        name: '',
        slug: '',
        description: ''
    });

    useEffect(() => {
        if (!tier) {
            setError('Invalid subscription tier');
            setLoading(false);
            return;
        }
        setLoading(true);
        const selectedPlan = pricingTiers.find(p => p.name.toLowerCase() === tier);
        if (!selectedPlan) {
            setError('Subscription plan not found');
            setPlan(null);
        } else {
            setPlan(selectedPlan);
        }
        setLoading(false);
    }, [tier]);

    const generateSlug = (name: string) => {
        return name
            .toLowerCase()
            .replace(/[^a-z0-9\s-]/g, '')
            .replace(/\s+/g, '-')
            .replace(/-+/g, '-')
            .trim();
    };

    const handleTeamNameChange = (name: string) => {
        setTeamData(prev => ({
            ...prev,
            name,
            slug: generateSlug(name)
        }));
    };

    const isTeamSubscription = tier === 'team';
    const requiresTeamData = isTeamSubscription;

    const validateForm = () => {
        if (requiresTeamData) {
            if (!teamData.name.trim()) {
                setError('Team name is required');
                return false;
            }
            if (!teamData.slug.trim()) {
                setError('Team slug is required');
                return false;
            }
            if (teamData.name.length < 2) {
                setError('Team name must be at least 2 characters');
                return false;
            }
            if (teamData.slug.length < 3) {
                setError('Team slug must be at least 3 characters');
                return false;
            }
        }
        return true;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!plan || !tier) {
            setError('Invalid subscription plan');
            return;
        }

        if (!validateForm()) {
            return;
        }

        try {
            setSubmitting(true);
            setError(null);

            const subscriptionData: SubscribeToTierRequest = {
                subscriptionTier: tier,
                ...(requiresTeamData && {
                    teamData: {
                        name: teamData.name.trim(),
                        slug: teamData.slug.trim(),
                        description: teamData.description.trim() || undefined
                    }
                })
            };

            // Use real Stripe Checkout flow
            const { url } = await billingService.createCheckoutSession(subscriptionData);
            window.location.href = url;

        } catch (err: any) {
            console.error('Subscription failed:', err);
            setError(err.message || 'Failed to process subscription');
        } finally {
            setSubmitting(false);
        }
    };

    const getTierIcon = (tier: string) => {
        switch (tier) {
            case 'free':
                return <CreditCard className="h-6 w-6" />;
            case 'starter':
                return <Star className="h-6 w-6" />;
            case 'team':
                return <Users className="h-6 w-6" />;
            default:
                return <CreditCard className="h-6 w-6" />;
        }
    };

    const formatPrice = (price: string) => {
        if (price === '$0' || price === 'Free') return 'Free';
        return price;
    };

    if (loading) {
        return (
            <div className="container mx-auto py-8">
                <div className="flex items-center justify-center">
                    <Loader2 className="h-8 w-8 animate-spin" />
                    <span className="ml-2">Loading subscription details...</span>
                </div>
            </div>
        );
    }

    if (!plan) {
        return (
            <div className="container mx-auto py-8">
                <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                        Subscription plan not found. Please go back and try again.
                    </AlertDescription>
                </Alert>
                <Button
                    variant="outline"
                    className="mt-4"
                    onClick={() => navigate('/app/billing')}
                >
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Back to Billing
                </Button>
            </div>
        );
    }

    if (success) {
        return (
            <div className="container mx-auto py-8">
                <Card className="max-w-md mx-auto">
                    <CardContent className="pt-6">
                        <div className="text-center space-y-4">
                            <CheckCircle className="h-16 w-16 text-green-500 dark:text-green-400 mx-auto" />
                            <div>
                                <h3 className="text-lg font-semibold">Subscription Successful!</h3>
                                <p className="text-muted-foreground">
                                    You've successfully subscribed to the {plan.name} plan.
                                </p>
                            </div>
                            <p className="text-sm text-muted-foreground">
                                Redirecting you back to billing...
                            </p>
                        </div>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <PageLayout nav={nav} footer={footer}>
            <div className="container mx-auto py-8 max-w-2xl">
                {/* Header */}
                <div className="flex items-center gap-4 mb-6">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate('/app/billing')}
                    >
                        <ArrowLeft className="h-4 w-4 mr-2" />
                        Back
                    </Button>
                    <div>
                        <h1 className="text-2xl font-bold">Subscribe to {plan.name}</h1>
                        <p className="text-muted-foreground">
                            Complete your subscription setup
                        </p>
                    </div>
                </div>

                {error && (
                    <Alert variant="destructive" className="mb-6">
                        <AlertCircle className="h-4 w-4" />
                        <AlertDescription>{error}</AlertDescription>
                    </Alert>
                )}

                <div className="space-y-6">
                    {/* Plan Summary */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                {getTierIcon(tier!)}
                                {plan.name} Plan
                            </CardTitle>
                            <CardDescription>
                                <div className="flex items-center gap-2">
                                    <span className="text-2xl font-bold">
                                        {formatPrice(plan.price)}
                                    </span>
                                </div>
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-3">
                                <h4 className="font-medium">Included features:</h4>
                                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                                    {plan.features.map((feature: any, index: number) => (
                                        <li key={index} className="flex items-start gap-2">
                                            <div className="w-1.5 h-1.5 bg-green-500 dark:bg-green-400 rounded-full mt-2 flex-shrink-0" />
                                            {feature}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Subscription Form */}
                    <form onSubmit={handleSubmit}>
                        <Card>
                            <CardHeader>
                                <CardTitle>Subscription Details</CardTitle>
                                <CardDescription>
                                    {requiresTeamData
                                        ? 'Create your team workspace as part of this subscription'
                                        : 'Complete your individual subscription'
                                    }
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-6">
                                {/* Billing Information Placeholder */}
                                <div className="p-4 bg-blue-50 dark:bg-blue-950/50 rounded-lg border border-blue-200 dark:border-blue-800">
                                    <h4 className="font-medium text-blue-900 dark:text-blue-100 mb-2">Billing Information</h4>
                                    <p className="text-sm text-blue-700 dark:text-blue-300 mb-3">
                                        For development purposes, we're using placeholder billing data.
                                        In production, this would integrate with Stripe for real payments.
                                    </p>
                                    <div className="grid grid-cols-2 gap-4 text-sm">
                                        <div>
                                            <span className="font-medium">Card:</span> •••• •••• •••• 4242
                                        </div>
                                        <div>
                                            <span className="font-medium">Exp:</span> 12/25
                                        </div>
                                    </div>
                                </div>

                                {/* Team Information (for team subscriptions) */}
                                {requiresTeamData && (
                                    <>
                                        <Separator />
                                        <div className="space-y-4">
                                            <div>
                                                <h4 className="font-medium mb-4">Team Information</h4>
                                                <div className="space-y-4">
                                                    <div className="space-y-2">
                                                        <Label htmlFor="teamName">Team Name *</Label>
                                                        <Input
                                                            id="teamName"
                                                            value={teamData.name}
                                                            onChange={(e) => handleTeamNameChange(e.target.value)}
                                                            placeholder="Enter your team name"
                                                            required
                                                        />
                                                    </div>

                                                    <div className="space-y-2">
                                                        <Label htmlFor="teamSlug">Team URL Slug *</Label>
                                                        <div className="flex">
                                                            <div className="flex items-center px-2 sm:px-3 bg-muted border border-r-0 rounded-l-md text-xs sm:text-sm text-muted-foreground whitespace-nowrap">
                                                                <span className="hidden sm:inline">arrowise.obprojects.dev/teams/</span>
                                                                <span className="sm:hidden">teams/</span>
                                                            </div>
                                                            <Input
                                                                id="teamSlug"
                                                                value={teamData.slug}
                                                                onChange={(e) => setTeamData(prev => ({ ...prev, slug: e.target.value }))}
                                                                placeholder="team-slug"
                                                                className="rounded-l-none flex-1 min-w-0"
                                                                pattern="^[a-z0-9-]+$"
                                                                required
                                                            />
                                                        </div>
                                                        <p className="text-xs text-muted-foreground">
                                                            Only lowercase letters, numbers, and hyphens allowed
                                                        </p>
                                                    </div>

                                                    <div className="space-y-2">
                                                        <Label htmlFor="teamDescription">Team Description</Label>
                                                        <Textarea
                                                            id="teamDescription"
                                                            value={teamData.description}
                                                            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setTeamData(prev => ({ ...prev, description: e.target.value }))}
                                                            placeholder="Describe your team's purpose (optional)"
                                                            rows={3}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </>
                                )}

                                {/* Submit Button */}
                                <div className="pt-4">
                                    <Button
                                        type="submit"
                                        className="w-full"
                                        disabled={submitting}
                                    >
                                        {submitting ? (
                                            <>
                                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                                Processing Subscription...
                                            </>
                                        ) : (
                                            <>
                                                <CreditCard className="h-4 w-4 mr-2" />
                                                Subscribe to {plan.name} Plan
                                            </>
                                        )}
                                    </Button>

                                    <p className="text-xs text-muted-foreground text-center mt-2">
                                        This is a test subscription with placeholder data
                                    </p>
                                </div>
                            </CardContent>
                        </Card>
                    </form>
                </div>
            </div>
        </PageLayout>
    );
};

export default SubscriptionForm;
